import { Conjunction } from '@/modules/common/enums/conjunction.enum.ts';
import { Direction } from '@/modules/common/enums/direction.enum.ts';
import { LuxonUtil } from '@/modules/common/utils/luxon.util.ts';
import Api, { type CacheConfig } from '@/modules/core/api/api.ts';
import type { ViewerCondition } from '@/modules/viewer/types/condition.type.ts';
import { ElementType } from '@/modules/viewer/enums/element-type.enum.ts';
import { Operator } from '@/modules/viewer/enums/operator.enum.ts';
import type { ViewerFilters, ViewerQuery, ViewerSearch, ViewerSort } from '@/modules/viewer/types/query.type.ts';
import type {
  ConditionRequest,
  ExecuteRequest,
  GroupRequest,
  NodeRequest,
  OrderByRequest,
  ProxyParamsRequest,
} from '@/modules/core/requests/execute.request.ts';
import { GatewayEndPoint } from '@/modules/core/enums/core-end-point-enum.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';
import type { DistinctRequest } from '@/modules/core/requests/distinct.request.ts';
import type { DistinctDto } from '@/modules/core/dto/distinct.dto.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';

export interface GatewayExecuteOptions {
  params?: ProxyParamsRequest;
  filter?: GroupRequest;
  limit?: number;
  offset?: number;
  orderBy?: OrderByRequest[];
  signal?: AbortSignal;
  mapData?: boolean;
  cacheConfig?: CacheConfig;
}

export interface GatewayGetOneOptions extends Omit<GatewayExecuteOptions, 'filter' | 'limit' | 'mapData'> {
  missing?: 'throw' | 'undefined';
}

export interface GatewayGetListOptions {
  mapGlobalFilter?: (search: ViewerSearch | null) => NodeRequest | null | undefined;
  defaultOrderBy?: OrderByRequest[];
}

export default class GatewayApi {
  private static mapToNode(nodes: NodeRequest[], logic: Conjunction): NodeRequest {
    if (nodes.length === 1) {
      return nodes[0];
    }

    return {
      logic,
      conditions: nodes,
    };
  }

  private static mapGlobalFilter(globalFilter: ViewerSearch | null) {
    if (!globalFilter?.query || !globalFilter.columns.length) {
      return null;
    }

    const conditions = globalFilter.columns.map((column) => GatewayFilter.contains(column, globalFilter.query));

    return GatewayApi.mapToNode(conditions, Conjunction.Or);
  }

  private static mapOperator({ type, operator }: ViewerCondition) {
    if (type === ElementType.Date) {
      return operator === Operator.Empty || operator === Operator.NotEmpty ? operator : Operator.Range;
    }

    return operator;
  }

  private static mapValue({ type, operator, value }: ViewerCondition) {
    switch (operator) {
      case Operator.Empty:
      case Operator.NotEmpty:
        return;
      case Operator.True:
      case Operator.False:
        return operator === Operator.True;
      case Operator.Equals:
        if (type === ElementType.Date) {
          return LuxonUtil.getSQLDateArray(value);
        } else {
          return value;
        }
      case Operator.Today:
        return LuxonUtil.getSQLDateArray(0);
      case Operator.Yesterday:
        return LuxonUtil.getSQLDateArray(-1);
      case Operator.Last7Days:
        return LuxonUtil.getSQLDateArray(-6, 0);
      case Operator.Last30Days:
        return LuxonUtil.getSQLDateArray(-29, 0);
      case Operator.Range:
        return LuxonUtil.getSQLDateArray(value.start, value.end);
      case Operator.In:
      case Operator.NotIn:
        return value.map((item) => item.code);
      default:
        return value;
    }
  }

  private static mapConditionToNode(field: string, condition: ViewerCondition): ConditionRequest {
    return GatewayFilter.condition(field, GatewayApi.mapOperator(condition), GatewayApi.mapValue(condition));
  }

  private static mapConditionsToNode(field: string, conditions: ViewerCondition[]) {
    const orBranches = conditions
      .reduce<ConditionRequest[][]>(
        (groups, condition) => {
          if (condition.conjunction === Conjunction.Or && groups[groups.length - 1].length) {
            groups.push([]);
          }

          groups[groups.length - 1].push(GatewayApi.mapConditionToNode(field, condition));

          return groups;
        },
        [[]]
      )
      .filter((group) => group.length > 0)
      .map((group) => GatewayApi.mapToNode(group, Conjunction.And));

    return orBranches.length ? GatewayApi.mapToNode(orBranches, Conjunction.Or) : undefined;
  }

  private static mapColumnFilters(columnFilters: ViewerFilters) {
    if (!columnFilters.length) {
      return null;
    }

    const conditions = columnFilters
      .map((filter) => GatewayApi.mapConditionsToNode(filter.id, filter.value))
      .filter((node): node is NodeRequest => !!node);

    return conditions.length ? GatewayApi.mapToNode(conditions, Conjunction.And) : null;
  }

  private static mapFilter(columnFilters: ViewerFilters, globalFilter: NodeRequest | null | undefined) {
    const conditions = [globalFilter, GatewayApi.mapColumnFilters(columnFilters)].filter((node) => !!node);

    return GatewayFilter.and(...conditions);
  }

  private static mapSorting(sort: ViewerSort | null) {
    if (sort) {
      return [
        {
          field: sort.id,
          direction: sort.desc ? Direction.Desc : Direction.Asc,
        },
      ] satisfies [OrderByRequest];
    }
  }

  static execute<T>(endpoint: string, options: GatewayExecuteOptions = {}): Promise<T> {
    const request: ExecuteRequest = { endpoint };

    if (options.params !== undefined) {
      request.proxy_params = options.params;
    }
    if (options.filter !== undefined) {
      request.proxy_filter = options.filter;
    }
    if (options.limit !== undefined) {
      request.proxy_limit = options.limit;
    }
    if (options.offset !== undefined) {
      request.proxy_offset = options.offset;
    }
    if (options.orderBy !== undefined) {
      request.proxy_orderby = options.orderBy;
    }

    return Api.post(
      GatewayEndPoint.Execute,
      { ...request },
      options.signal,
      options.mapData ?? true,
      options.cacheConfig
    );
  }

  static getList<T>(
    endpoint: string,
    query: ViewerQuery,
    signal: AbortSignal,
    { mapGlobalFilter = GatewayApi.mapGlobalFilter, defaultOrderBy }: GatewayGetListOptions = {}
  ): Promise<ItemsDto<T>> {
    return GatewayApi.execute<ItemsDto<T>>(endpoint, {
      limit: query.page.limit,
      offset: query.page.offset,
      filter: GatewayApi.mapFilter(query.filters, mapGlobalFilter(query.search)),
      orderBy: GatewayApi.mapSorting(query.sort) ?? defaultOrderBy,
      signal,
    });
  }

  static async getOne<T>(
    endpoint: string,
    filter: GroupRequest,
    options: GatewayGetOneOptions & { missing: 'throw' }
  ): Promise<T>;
  static async getOne<T>(
    endpoint: string,
    filter: GroupRequest,
    options?: GatewayGetOneOptions
  ): Promise<T | undefined>;
  static async getOne<T>(
    endpoint: string,
    filter: GroupRequest,
    options: GatewayGetOneOptions = {}
  ): Promise<T | undefined> {
    const { missing = 'undefined', ...executeOptions } = options;
    const response = await GatewayApi.execute<ItemsDto<T>>(endpoint, {
      ...executeOptions,
      filter,
      limit: 1,
    });
    const item = response.items[0];

    if (!item && missing === 'throw') {
      throw new Error(`Item not found for ${endpoint}`);
    }

    return item;
  }

  static async get<T>(endpoint: string, field: string, value: number | string | boolean, signal?: AbortSignal) {
    const item = await GatewayApi.getOne<T>(endpoint, GatewayFilter.and(GatewayFilter.eq(field, value))!, { signal });

    if (!item) {
      throw new Error(`Item not found for ${endpoint} with ${field} = ${value}`);
    }

    return item;
  }

  static async getDistinct(
    endpoint: string,
    fields: string[],
    signal?: AbortSignal
  ): Promise<Record<string, string[]>> {
    const response: DistinctDto = await Api.post(
      GatewayEndPoint.Distinct,
      {
        endpoint,
        proxy_distinct_fields: fields,
      } satisfies DistinctRequest,
      signal
    );

    return response.items;
  }
}
